import { type ReactNode, type MouseEvent } from 'react';
import clsx from 'clsx';
import { useDroppable } from '@dnd-kit/core';
import { format, dfIsSameDay } from '~/util/date';
import type { CellRenderer } from './types';

export interface DayCellProps {
	date: Date;
	isOutOfRange?: boolean;
	today?: Date;
	onCellClick?: (date: Date) => void;
	renderCellContent?: CellRenderer;
	renderDayLabel?: (date: Date) => ReactNode;
	droppable?: boolean;
	isValidDropTarget?: boolean;
	eventLayout?: 'wrap' | 'stack';
	minHeight?: number;
	showDayName?: boolean;
	children?: ReactNode;
}

function formatDayNumber(date: Date) {
	const day = date.getDate();
	return String(day);
}

function toIsoDate(date: Date) {
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, '0');
	const d = String(date.getDate()).padStart(2, '0');
	return `${y}-${m}-${d}`;
}

export function DayCell({
	date,
	isOutOfRange = false,
	today,
	onCellClick,
	renderCellContent,
	renderDayLabel,
	droppable = false,
	isValidDropTarget,
	eventLayout = 'wrap',
	minHeight = 88,
	showDayName = false,
	children
}: DayCellProps) {
	const isToday = today ? dfIsSameDay(date, today) : false;
	const isoDate = toIsoDate(date);

	const { setNodeRef, isOver } = useDroppable({
		id: `cell:${isoDate}`,
		data: { date },
		disabled: !droppable || isOutOfRange || isValidDropTarget === false
	});

	const handleClick = (e: MouseEvent<HTMLDivElement>) => {
		if ((e.target as HTMLElement).closest('[data-event-chip]')) return;
		if (isOutOfRange) return;
		onCellClick?.(date);
	};

	return (
		<div
			ref={setNodeRef}
			data-date={isoDate}
			onClick={handleClick}
			style={{ minHeight }}
			className={clsx(
				'flex flex-col gap-1 border-b border-r border-gray-200 p-2 text-left transition-colors',
				!isOutOfRange && 'cursor-pointer hover:bg-gray-50',
				isOutOfRange && 'bg-gray-50/50',
				isValidDropTarget === true && 'bg-primary-50/60',
				isValidDropTarget === false && 'opacity-50',
				isOver && !isOutOfRange && 'bg-primary-50 ring-2 ring-inset ring-primary-400'
			)}
		>
			<div className="flex items-baseline gap-2">
				<div
					className={clsx(
						'text-xs font-medium',
						isOutOfRange && 'text-gray-300',
						!isOutOfRange && !isToday && 'text-gray-700',
						isToday && 'inline-flex size-6 items-center justify-center rounded-full bg-gray-500 text-white'
					)}
				>
					{formatDayNumber(date)}
				</div>
				{showDayName && (
					<div className={clsx('text-xs font-medium uppercase tracking-wide', isOutOfRange ? 'text-gray-300' : 'text-gray-500')}>
						{format(date, 'EEE')}
					</div>
				)}
				{renderDayLabel && (
					<div className={clsx('text-xs font-medium uppercase tracking-wide', isOutOfRange ? 'text-gray-300' : 'text-gray-500')}>
						{renderDayLabel(date)}
					</div>
				)}
			</div>
			{renderCellContent?.({ date, isOutOfRange })}
			{!isOutOfRange && <div className={clsx(eventLayout === 'wrap' ? 'flex flex-wrap gap-1' : 'flex flex-col gap-1')}>{children}</div>}
		</div>
	);
}
